import React, {memo, useEffect, useState} from 'react';
import {makeStyles, Theme, createStyles} from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import MeasureIcon from '@material-ui/icons/SquareFoot';
import SettingsIcon from '@material-ui/icons/Settings';
import EditIcon from '@material-ui/icons/Edit';
import {
  changeName,
  selectName
} from "./redux/project-slice";
import {useSelector, useDispatch} from 'react-redux';
import {
  Projection,
  changeDataProjection,
  changeViewProjection,
  selectDataProjection
} from './redux/projections-slice';
import {
  userProjections,
  uniqueProjectionID,
  unknownProjectionFeet,
  unknownProjectionMeters
} from './projections';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  TextField
} from '@material-ui/core';
import {assetDrawerWidth} from './asset-drawer';
import LocalScene, {
  projectionToMeters,
  projectionToUnits,
  SceneCoordinate
} from './viewer/js/projections';
import {dialog, isStaticSite} from "./electron-modules";
import slash from 'slash';
import {
  changeBasicAdjustments,
  changeCameraHeight,
  changeCameraTransform,
  selectBasicAdjustments,
  selectCameraHeight,
  selectCameraTransform
} from './redux/camera-slice';
import {
  asyncTimeout,
  getMatrixValues,
  saveMatrixFile
} from "./utilities";
import {cameraMatrixFilter} from "./file-extensions";
import clsx from 'clsx';
import { ArrowTooltip } from './components';
import { Viewer } from './viewer/js/main';
import {headerHeight, toast} from './app';
import {settingsDrawerWidth} from "./settings";
import { sendCustomEvent } from './events';
import { Vector3 } from 'three';
import { useTranslation } from 'react-i18next';
import { t } from './localization';
import { deleteBookmarks } from './redux/bookmarks-slice';
import { changeSelectedTab } from './redux/settings-slice';
import { UnitConverter } from './viewer/js/utilities';

const converter = new UnitConverter();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    disabled: {
      opacity: 0.3
    },
    warning: {
      color: theme.palette.warning.main
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    title: {
      flexGrow: 1,
    },
    projectName: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
      justifyContent: 'flex-start',
      flexGrow: 1
    },
    projectionText: {
      paddingTop: theme.spacing(0),
      marginTop: "-5px",
      color: "white"
    },
    marginTop: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1)
    },
    buttonDiv: {
      textAlign: "right"
    },
    footerCoords: {
      fontSize: "0.95rem",
      paddingRight: theme.spacing(1.5),
      textShadow: "1px 1px 2px black, 0px 0px 2px black"
    },
    footerOffset: {
      marginRight: settingsDrawerWidth
    },
    footer: {
      boxShadow: "none",
      background: "transparent",
      pointerEvents: "none"
    },
    coordinates: {
      width: "100%",
      display: "flex",
      justifyContent: "flex-end"
    },
    button: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1)
    },
    slimButton: {
      marginTop: theme.spacing(1)
    },
    dropdown: {
      minWidth: 200,
    },
    offset: {
      marginLeft: assetDrawerWidth - 60
    },
    textField: {
      paddingBottom: theme.spacing(3)
    },
    matrixValuesParent: {
      display: "flex",
      paddingTop: theme.spacing(2),
      justifyContent: "space-evenly"
    },
    matrixValues: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center"
    },
    matrixButtons: {
      display: "flex",
      justifyContent: "center"
    }
  }),
);

const openDialogMatrix = (): Promise<string> => {
  return dialog.showOpenDialog( {
    filters: cameraMatrixFilter,
    properties: ['openFile']
  }).then(result => {
    if (result.canceled) {
      return null;
    }

    const filePath = slash(result.filePaths[0]);
    return filePath;
  }).catch(() => {
    dialog.showErrorBox(t("dialog.general-error-title"), t("dialog.file-load-error"));
    return [];
  });
};

const ProjectDetails = (props) => {
  const viewer = props.viewer as Viewer;
  const {assetDrawerState, setMeasurementsOpen} = props;

  const dispatch = useDispatch();
  const classes = useStyles();
  const {t} = useTranslation();

  const [open, setOpen] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);
  const [name, setName] = useState("");
  const [height, setHeight] = useState(0);
  const [proj, setProj] = useState("");
  const [imported, setImported] = useState<Projection>(null);

  const [cameraTransform, setCameraTransform] = useState({
    path: null,
    rotation: null,
    offset: null
  });

  const [errors, setErrors] = useState({
    name: false,
    height: false,
    projection: false,
    correction: false
  });

  const projectName = useSelector(selectName);
  const projectHeight = useSelector(selectCameraHeight);
  const cameraTransformPath = useSelector(selectCameraTransform);
  const dataProjection = useSelector(selectDataProjection);
  const basicAdjustments = useSelector(selectBasicAdjustments);

  const unknownTextMeters = unknownProjectionMeters.name;
  const unknownTextFeet = unknownProjectionFeet.name;

  const getNewProjection = () => {
    let newProjection = null;

    if (proj === unknownTextMeters) {
      newProjection = unknownProjectionMeters;
    } else if (proj === unknownTextFeet) {
      newProjection = unknownProjectionFeet;
    } else if (imported && (proj === imported.name)) {
      newProjection = imported;
    } else {
      const results = data.filter(x => x.name === proj);
      if (results.length !== 1) {
        return;
      }
      newProjection = results[0];
    }

    return newProjection;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleWarnClose = () => {
    setWarnOpen(false);
  };

  const updateName = (event) => {
    const value = event.target.value;
    setErrors({...errors, name: false});
    setName(value);
  };

  const updateHeight = (event) => {
    const value = event.target.value;
    setErrors({...errors, height: false});
    setHeight(value);
  };

  const updateProj = (event) => {
    const value = event.target.value;
    if (!value) {
      return;
    }

    setErrors({...errors, projection: false});
    setProj(value);
  };

  const onSubmit = async () => {
    const trimmedName = name.trim();
    const emptyName = trimmedName === "";
    const emptyHeight = (height as any) === "";
    const emptyProjection = proj === "";

    setErrors({
      name: emptyName,
      height: emptyHeight,
      projection: emptyProjection,
      correction: false
    });

    if (emptyName || emptyProjection || emptyHeight) {
      return;
    }

    const heightInMeters = height * toMeters;

    setOpen(false);
    toast.success(t("toast.project-details-updated"));

    await asyncTimeout(100);

    dispatch(changeName(trimmedName));
    dispatch(changeCameraHeight(heightInMeters));
    dispatch(changeCameraTransform(cameraTransform.path));

    // Projection change requires a more strict check before dispatch
    const projectionName = selectedProjection.name;
    const projectionChanged = !LocalScene.isDataProjection(projectionName);

    if (!projectionChanged) return;

    viewer.setInitialStateFlag(false); // Reset initial zoom
    dispatch(changeSelectedTab(0)); // Reset to manage tab
    setMeasurementsOpen(false); // Clear all measurements
    dispatch(deleteBookmarks()); // Clear all bookmarks

    dispatch(changeDataProjection(selectedProjection));
    dispatch(changeViewProjection(selectedProjection));
  };

  const handleCollisions = () => {
    const name = dataProjection.name;
    if ((name === unknownTextFeet) || (name === unknownTextMeters)) {
      return;
    }

    if (imported?.name === name) {
      return;
    }

    const results = userProjections.data.filter(x => x.name === name);

    if (results.length > 0) {
      // Compare projection with existing projections
      const existing = results[0];
      const importedProjection = JSON.stringify(dataProjection, Object.keys(dataProjection).sort());
      const userProjection = JSON.stringify(existing, Object.keys(existing).sort());

      if (importedProjection !== userProjection) {
        // Projections are different, so we warn the user
        setWarnOpen(true);
      }
    }

    setImported({...dataProjection, imported: true});
  };

  const onCorrectionSelect = async () => {
    const filePath = await openDialogMatrix();
    if (!filePath) return;

    const {success, rotation, offset} = await getMatrixValues(filePath);

    if (!success) {
      toast.error(t("toast.correction-matrix-error"));
      return;
    }

    setCameraTransform({path: filePath, rotation, offset});
  };

  const onMatrixChange = async (filePath: string) => {
    const {success, rotation, offset} = await getMatrixValues(filePath);

    if (success) {
      setCameraTransform({path: filePath, rotation, offset});
    } else {
      onCorrectionRemove();
    }
  };

  const onCorrectionRemove = () => {
    setCameraTransform({
      path: null,
      rotation: null,
      offset: null
    });
  };

  const updateTitle = (title) => {
    document.title = `SOLV3D engine viewer : ${title}`;
  };

  const clearBasicAlignment = () => {
    dispatch(changeBasicAdjustments({}));
    viewer?.resetBasicAlignerCamera();
  };

  const getTransformValues = () => {
    let rotation = [0,0,0];
    let offset = [0,0,0];

    if (cameraTransform.offset) {
      offset = [
        converter.convert(cameraTransform.offset.x, "m", units, 2),
        converter.convert(cameraTransform.offset.y, "m", units, 2),
        converter.convert(cameraTransform.offset.z, "m", units, 2)
      ];
    }

    if (cameraTransform.rotation) {
      rotation = [
        cameraTransform.rotation.x.toFixed(2),
        cameraTransform.rotation.y.toFixed(2),
        cameraTransform.rotation.z.toFixed(2)
      ];
    }

    return {rotation, offset};
  };

  useEffect(() => {
    setProj(dataProjection.name);
  }, [dataProjection]);

  useEffect(() => {
    setName(projectName);
  }, [projectName]);

  useEffect(() => {
    onMatrixChange(cameraTransformPath);
  }, [cameraTransformPath]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const height = projectHeight / toMeters;
    setHeight(height);
  }, [open]);

  handleCollisions();

  const data = userProjections.data;
  data.sort((a, b) => {
    const name1 = a.name.toLowerCase();
    const name2 = b.name.toLowerCase();
    return (name1 < name2) ? -1 : ((name2 > name1) ? 1 : 0);
  });

  const matchingProjection = userProjections.data?.filter(
    x => x.name === dataProjection.name)[0];

  const selectedProjection = getNewProjection();
  const units = projectionToUnits(selectedProjection);
  const toMeters = projectionToMeters(selectedProjection);
  const hasBasicAlignment = Object.keys(basicAdjustments).length !== 0;

  const localizedProjectName = t(projectName);
  const localizedNameValue = t(name);

  const isDefaultProjection = dataProjection.default;
  const localizedProjectionName = isDefaultProjection
    ? t(`projections.default-projection`, {units})
    : dataProjection.name;


  const {rotation, offset} = getTransformValues();
  updateTitle(localizedProjectName);

  return (
    <div className={classes.projectName}>
      <FormControl
        component="fieldset"
        className={assetDrawerState ? classes.offset : ""}
      >
        <Typography variant="h6" noWrap>
          {localizedProjectName}
        </Typography>
        <FormHelperText
          error={isDefaultProjection}
          className={classes.projectionText}
        >
          {localizedProjectionName}
        </FormHelperText>
      </FormControl>

      {(!isStaticSite) && (<ArrowTooltip title={t('project-settings.edit_project')}>
        <IconButton
          onClick={() => {
            setOpen(true);
          }}
          color={"inherit"}
        >
          <EditIcon fontSize={"small"}/>
        </IconButton>
      </ArrowTooltip>)}

      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        onClose={handleClose}
      >
        <DialogTitle>
          {t('project-settings.project-information')}
        </DialogTitle>
        <DialogContent>

          {/* Project display title */}
          <InputLabel>
            {t('project-settings.project-title')}
          </InputLabel>

          <Typography variant="caption">
            {t('project-settings.project-title-description')}
          </Typography>
          <TextField
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            value={localizedNameValue}
            error={errors.name}
            onChange={updateName}
            className={classes.textField}
          />

          {/* Camera height */}
          <InputLabel>
            {t('project-settings.camera-height', {units})}
          </InputLabel>
          <Typography variant="caption">
            {t('project-settings.camera-height-description')}
          </Typography>
          <TextField
            InputLabelProps={{
              shrink: true,
            }}
            type="number"
            fullWidth
            value={height}
            error={errors.height}
            onChange={updateHeight}
            className={classes.textField}
          />

          {/* Projection dropdown */}
          <InputLabel>
            {t('project-settings.data-projection')}
          </InputLabel>
          <Typography variant="caption">
            {t('project-settings.data-projection-description')}
          </Typography>
          <TextField
            select
            fullWidth
            value={proj}
            error={errors.projection}
            onChange={updateProj}
            className={classes.textField}
          >
            {imported && (<ListSubheader>
              {t('project-settings.imported-projections')}
            </ListSubheader>)}

            {imported && (<MenuItem
              key={uniqueProjectionID(imported.name)}
              value={imported.name}
            >{imported.name}</MenuItem>)}

            <ListSubheader>
              {t('project-settings.generic-projections')}
            </ListSubheader>

            <MenuItem
              key={uniqueProjectionID(unknownTextMeters)}
              value={unknownTextMeters}
            >{t("projections.default-projection", {units: "m"})}</MenuItem>

            <MenuItem
              key={uniqueProjectionID(unknownTextFeet)}
              value={unknownTextFeet}
            >{t("projections.default-projection", {units: "ft"})}</MenuItem>

            <ListSubheader>
              {t('project-settings.user-projections')}
            </ListSubheader>

            {data.map(row => {
              const conflict = imported ? imported.name === row.name : false;
              const suffix = conflict ? "__conflict" : "";
              return (<MenuItem
                key={uniqueProjectionID(row.name) + suffix}
                value={row.name + suffix}
                disabled={conflict}
                title={conflict ? t('project-settings.projection-conflict'): ""}
              >{row.name}</MenuItem>);
            })}
          </TextField>

          {/* Camera Correction Matrix */}
          <div>
            <InputLabel>
              {t('project-settings.camera-correction')}
            </InputLabel>

            {(!hasBasicAlignment) && (<Typography variant="caption">
              {t('project-settings.camera-correction-description')}
            </Typography>)}

            {(hasBasicAlignment) && (<div className={classes.marginTop}>
              <Typography variant="caption" className={classes.warning}>
                {t('project-settings.camera-correction-warning')}
              </Typography>
            </div>)}

            <div>
              {cameraTransform.path && <div className={classes.matrixValuesParent}>
                <Typography variant="caption" className={classes.matrixValues}>
                  <b>{t('project-settings.matrix_offset_units', {units})}: </b> [{offset[0]}, {offset[1]}, {offset[2]}]
                </Typography>

                <Typography variant="caption" className={classes.matrixValues}>
                  <b>{t('project-settings.matrix_rotation')}: </b> [{rotation[0]}, {rotation[1]}, {rotation[2]}]
                </Typography>
              </div>}

              <div className={classes.matrixButtons}>
                {/* Save matrix file */}
                {(cameraTransform.path) && (<Button
                  type="submit"
                  onClick={()=>saveMatrixFile(cameraTransform.path)}
                  className={clsx(classes.button, classes.slimButton)}
                  color="primary"
                >
                  {t('buttons.save-file')}
                </Button>)}

                {/* Remove matrix file */}
                {cameraTransform.path && (<Button
                  type="submit"
                  onClick={onCorrectionRemove}
                  className={clsx(classes.button, classes.slimButton)}
                  color="secondary"
                >
                  {t('buttons.remove-file')}
                </Button>)}

                {/* Upload matrix file */}
                {(!hasBasicAlignment) && (<Button
                  type="submit"
                  onClick={onCorrectionSelect}
                  className={clsx(classes.button, classes.slimButton)}
                  color="primary"
                >
                  {t('buttons.upload-file')}
                </Button>)}

                {/* Cancel basic alignment */}
                {(hasBasicAlignment) && (<Button
                  onClick={clearBasicAlignment}
                  color="secondary"
                >
                  {t('buttons.clear-file')}
                </Button>)}
              </div>
            </div>

          </div>

          <div className={classes.buttonDiv}>
            <Button
              color="primary"
              type="submit"
              onClick={() => {
                setOpen(false);
              }}
              className={classes.button}
            >
              {t('buttons.cancel')}
            </Button>

            <Button
              color="primary"
              type="submit"
              onClick={onSubmit}
              className={classes.button}
            >
              {t('buttons.update-project')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Warning message for projection mismatch */}
      <Dialog
        open={warnOpen}
        onClose={handleWarnClose}
      >
        <DialogTitle>
          {t('project-settings.projection-mismatch')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('project-settings.projection-mismatch-description', {name: dataProjection.name})}
          </DialogContentText>

          <DialogContentText>
            <b>{t('project-settings.imported-projection')}</b>
            <br></br>
            "{dataProjection.string}"
          </DialogContentText>

          <DialogContentText>
            <b>{t('project-settings.existing-projection')}</b>
            <br></br>
            "{matchingProjection?.string}"
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarnClose} color="primary">
            {t('buttons.dismiss')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export const MainAppBar = memo((props: any) => {
  const classes = useStyles();

  const viewer = props.viewer as Viewer;

  const {settingsDrawerState, setSettingsDrawerState,
    assetDrawerState, setAssetDrawerState, measurementsOpen,
    setMeasurementsOpen, canOpenTool} = props;

  return (
    <AppBar position="static" style={{minHeight: headerHeight}}>
      <Toolbar>

        <IconButton
          edge="start"
          className={classes.menuButton}
          color="inherit"
          onClick={() => {
            setAssetDrawerState(true);
          }}
        >
          <MenuIcon/>
        </IconButton>

        <ProjectDetails
          viewer={viewer}
          assetDrawerState={assetDrawerState}
          setMeasurementsOpen={setMeasurementsOpen}
        />

        <ArrowTooltip title={t('app-bar.measurements')}>
          <IconButton
            color="inherit"
            className={measurementsOpen ? classes.disabled : ""}
            onClick={() => {
              if (!canOpenTool) return toast.warning(t('toast.one_tool_only'));

              setMeasurementsOpen(true);
            }}
          >
            <MeasureIcon/>
          </IconButton>
        </ArrowTooltip>

        <ArrowTooltip title={t('app-bar.settings')}>
          <IconButton
            color="inherit"
            className={settingsDrawerState ? classes.disabled : ""}
            onClick={() => {
              setSettingsDrawerState(true);
            }}
          >
            <SettingsIcon/>
          </IconButton>
        </ArrowTooltip>

      </Toolbar>
    </AppBar>
  );
});

export const MainAppFooter = memo((props: any) => {
  const classes = useStyles();

  const viewer = props.viewer as Viewer;
  const {settingsDrawerState} = props;

  const [mouseCoords, setMouseCoords] = useState(null);
  const [coordData, setCoordData] = useState({});
  const formatter = viewer?.getMeasurementFormatter();

  useEffect(() => {
    setCoordinates();
  }, [mouseCoords]);

  /** Mouse coordinate event listener */
  useEffect(() => {
    const callback = (event) => {
      const {coords} = event.detail;
      setMouseCoords(coords);
    };

    document.addEventListener("update-mouse-coords", callback);

    return () => {
      document.removeEventListener("update-mouse-coords", callback);
    };
  }, []);

  const setCoordinates = () => {
    if (!mouseCoords || !LocalScene.initialized) {
      setCoordData({});
      return;
    }

    const precision = formatter.getVectorPrecision(LocalScene.viewProjection);
    const coordinate = new SceneCoordinate(mouseCoords)
      .toViewProjection();

    const x = coordinate.x.toFixed(precision.x);
    const y = coordinate.y.toFixed(precision.y);
    const z = coordinate.z.toFixed(precision.z);

    setCoordData({x, y, z});
  };

  return (
    <AppBar className={classes.footer} position="static">
      <Toolbar disableGutters>
        <div className={clsx(classes.coordinates, {
          [classes.footerOffset]: settingsDrawerState
        })}>
          {Object.keys(coordData).map((key) => (
            <Typography key={key} className={classes.footerCoords}>
              <b>{`${key.toUpperCase()}:`}</b> {coordData[key]}
            </Typography>
          ))
          }
        </div>
      </Toolbar>
    </AppBar>
  );
});

export const updateMouseCoords = (coords: Vector3) => {
  sendCustomEvent("update-mouse-coords", {coords});
};